
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { shiftScrapRecordsStore, shiftsStore, shiftStationsStore, shiftFilesStore } from '@/libs/shifts/+state/store';
import { CrudAction, CrudReponse, CrudResponseAction } from '@/libs/core/+state/models/crud-action';
import {
    AuditModel,
    AuditShiftTypeModel,
    AuditWorkplaceModel,
    FileDownloadModel,
    PlanItemModel,
    ProcessTypeEnum,
    ShiftModel,
    ShiftProductionModel,
    ShiftProductionRecordModel,
    ShiftScrapRecordModel,
    ShiftStateEnum,
    ShiftStationModel,
    TenantUserModel,
} from '@/libs/Api';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { Guid } from '@/libs/common/functions/guid';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { ApexOptions } from 'apexcharts';
import moment from 'moment';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import { auditWorkplacesStore } from '@/libs/audit-workplaces/+state/store';
import { auditShiftTypesStore } from '@/libs/audit-shift-types/+state/store';
import { auditsStore } from '@/libs/audits/+state/store';
import MeasureList from '@/libs/shifts/components/MeasureList.vue';
import EditShiftDetailDialog from '@/libs/shifts/components/EditShiftDetailDialog.vue';
import ApiService from '@/libs/core/api-service';
import ShiftState from '@/libs/shifts/components/ShiftState.vue';
import ShiftScrapRecordDialog from '@/libs/shifts/components/ShiftScrapRecordDialog.vue';
import axios from 'axios';
import { getAccessToken } from 'axios-jwt';
import imageCompression from 'browser-image-compression';
import { tenantsStore } from '@/libs/tenants/+state/store';
import { FileModel, ShiftFileModel, TenantModel } from '@/libs/Api';

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
};

@Component({
    components: { ShiftScrapRecordDialog, ShiftState, EditShiftDetailDialog, MeasureList },
})
export default class ShiftManagementDetail extends Page {
    @Prop() shiftId!: string | null;
    @Prop({ default: true }) canClose!: boolean;
    @Ref() chart!: any;
    fromDate: Date | null = null;
    toDate: Date | null = null;

    protected isPrinting = false;
    protected isSaving = false;
    protected isSavingComment = false;
    protected editDialogTab = 1;
    protected editDialog = false;
    protected commentIsChanged = false;
    protected comment: string | null = null;
    protected snackbarFileTooBig = false;
    protected isUploadingShiftFile = false;
    protected maxUploadFileSize = 50;
    protected deletingShiftFile: { [key: string]: boolean } = {};

    private shiftScrapRecordDialog = false;
    private activeScrapRecordId: string | null = null;
    private activeScrapRecordToDelete: ShiftScrapRecordModel | null = null;
    private shiftScrapRecordDeleteDialog = false;

    protected ShiftStateEnum: typeof ShiftStateEnum = ShiftStateEnum;

    @Ref() inputShiftFiles!: any;

    get showDetails() {
        return this.fromDate && this.toDate;
    }

    get emptyGuid(): string {
        return Guid.EmptyGuid();
    }

    get isLoadingShift(): boolean {
        return shiftsStore.useGetter(CrudGetter.Pending);
    }

    get isLoadingChecklists(): boolean {
        return (
            auditsStore.useGetter(CrudGetter.Pending, 'AvailableChecklists') ||
            auditWorkplacesStore.useGetter(CrudGetter.Pending, 'AvailableChecklists') ||
            auditShiftTypesStore.useGetter(CrudGetter.Pending, 'AvailableChecklists') ||
            planItemsStore.useGetter(CrudGetter.Pending, 'PerformedChecklists')
        );
    }

    get isLoadingShutdowns(): boolean {
        return planItemsStore.useGetter(CrudGetter.Pending, 'LocationShiftDetailShutdowns');
    }

    get isLoadingIssueSolving(): boolean {
        return planItemsStore.useGetter(CrudGetter.Pending, 'LocationShiftDetail');
    }

    get isLoadingPerformedMaintenance(): boolean {
        return planItemsStore.useGetter(CrudGetter.Pending, 'LocationShiftDetailMaintenance');
    }

    get shift(): ShiftModel {
        return shiftsStore.useGetter(CrudGetter.Detail);
    }

    get issueSolvings(): PlanItemModel[] {
        return planItemsStore.useGetter(CrudGetter.Data, 'LocationShiftDetail') ?? [];
    }

    get performedMaintenance(): PlanItemModel[] {
        return planItemsStore.useGetter(CrudGetter.Data, 'LocationShiftDetailMaintenance') ?? [];
    }

    get isLoadingShiftStations(): ShiftStationModel[] {
        return shiftStationsStore.useGetter(CrudGetter.Pending, 'LocationShiftDetail');
    }

    get isDeletingShiftScrapRecord(): boolean {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Deleting) ?? false;
    }

    get shiftStations(): ShiftStationModel[] {
        const items: ShiftStationModel[] = shiftStationsStore.useGetter(CrudGetter.Data, 'LocationShiftDetail') ?? [];
        return items.sort((a, b) => (a.station?.name ?? '').localeCompare(b.station?.name ?? ''));
    }

    get chartOptions(): ApexOptions {
        console.log(this.getChartSeries().map((x) => (x.identification == 'uptime' ? '#27a800' : '#FF0000')));
        return {
            chart: {
                type: 'bar',
                height: 100,
                stacked: true,
                toolbar: {
                    show: false,
                },
                parentHeightOffset: 0,
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                },
            },
            xaxis: {
                categories: [1],
                labels: {
                    formatter: (value: string, timestamp?: number, opts?: any): string | string[] => {
                        return moment(new Date(this.fromDate!.getTime() + value)).format('HH:mm');
                    },
                },
                stepSize: 3600000,
            },
            yaxis: {
                labels: {
                    show: false,
                    formatter: (val: number, opts?: any): string | string[] => {
                        const chartSeries = this.getChartSeries();
                        const item = chartSeries[opts.seriesIndex];
                        if (!item) {
                            return '';
                        }
                        return `${
                            item!.identification == 'shutdown' ? this.$t('shift.shutdown') : this.$t('shift.production')
                        } ${this.$t('common.from')} ${moment(item.fromTime).format('DD.MM.YYYY HH:mm')!} ${this.$t(
                            'common.to'
                        )} ${moment(item.toTime).format('DD.MM.YYYY HH:mm')!}`;
                    },
                },
                min: 0,
                max: this.toDate!.getTime() - this.fromDate!.getTime(),
                stepSize: 100,
            },
            dataLabels: {
                formatter(val: string | number | number[], opts?: any): string | number {
                    return '';
                },
            },
            tooltip: {
                x: {
                    show: false,
                },
                marker: {
                    show: false,
                },
            },
            fill: {
                type: 'solid',
            },
            legend: {
                show: false,
            },
            colors: this.getChartSeries().map((x) => (x.identification == 'uptime' ? '#27a800' : '#FF0000')),
        };
    }

    get hourChartOptions(): ApexOptions {
        return {
            chart: {
                type: 'bar',
                height: 100,
                toolbar: {
                    show: false,
                },
                parentHeightOffset: 0,
                zoom: {
                    enabled: false,
                },
            },
            plotOptions: {
                bar: {
                    columnWidth: '50%',
                },
            },
            xaxis: {
                type: 'category',
                categories: this.getShiftHours().map((x) => x.name),
                labels: {
                    formatter: (value: string): string | string[] => {
                        const chartSeries = this.getShiftHours();
                        const item = chartSeries.filter((x) => x.fromDate <= Number(value) && x.toDate > Number(value)).shift();
                        if (!item) {
                            return '';
                        }
                        return item.name;
                    },
                    offsetX: -3,
                },
            },
            yaxis: {
                labels: {
                    show: false,
                    formatter: (val: number): string | string[] => {
                        return val.toString();
                    },
                },
            },
            dataLabels: {
                formatter(val: string | number | number[]): string | number {
                    return '';
                },
            },
            tooltip: {
                x: {
                    show: false,
                },
                marker: {
                    show: false,
                },
            },
            fill: {
                type: 'solid',
            },
            legend: {
                show: false,
            },
            colors: ['#27a800', '#FF0000'],
        };
    }

    get chartSeries(): any {
        return this.getChartSeries();
    }

    get hourChartSeries(): any {
        const shift = shiftsStore.useGetter(CrudGetter.Detail) as ShiftModel;
        if (!shift) {
            return [];
        }
        const items = (shift.shiftProductions ?? [])
            .reduce((acc, x) => {
                acc = [...acc, ...(x.shiftProductionRecords ?? [])];
                return acc;
            }, [] as ShiftProductionRecordModel[])
            .groupBy((x) => new Date(x.recordTime!).getTime())
            .select((x) => {
                return {
                    recordTime: x.first().recordTime,
                    fromDate: moment(x.key).subtract(1, 'hours').toDate().getTime(),
                    toDate: moment(x.key).toDate().getTime(),
                    okCount: x.select((n) => n.okCount ?? 0).sum(),
                    nokCount: x.select((n) => n.nokCount ?? 0).sum(),
                };
            })
            .toArray();
        return [
            {
                name: 'OK',
                data: this.getShiftHours().map((e) => {
                    return [
                        new Date(e.fromDate).getTime(),
                        items.filter((x) => x.fromDate < e.toDate && x.toDate > e.fromDate).sum((x) => x.okCount ?? 0),
                    ];
                }),
            },
            {
                name: 'NOK',
                data: this.getShiftHours().map((e) => {
                    return [
                        new Date(e.fromDate).getTime(),
                        items.filter((x) => x.fromDate < e.toDate && x.toDate > e.fromDate).sum((x) => x.nokCount ?? 0),
                    ];
                }),
            },
        ];
    }

    get checkLists(): { audit: AuditModel; planItem: PlanItemModel | null }[] {
        const performedChecklists: PlanItemModel[] =
            planItemsStore.useGetter(CrudGetter.DataBasic, 'PerformedChecklists') ?? [];
        console.log(performedChecklists);
        const shift: ShiftModel | null = shiftsStore.useGetter(CrudGetter.Detail) ?? null;
        const auditWorkplaces: AuditWorkplaceModel[] =
            auditWorkplacesStore.useGetter(CrudGetter.Data, 'AvailableChecklists') ?? [];
        const auditShiftTypes: AuditShiftTypeModel[] =
            auditShiftTypesStore.useGetter(CrudGetter.Data, 'AvailableChecklists') ?? [];
        return ((auditsStore.useGetter(CrudGetter.Data, 'AvailableChecklists') ?? []) as AuditModel[])
            .filter((x) => {
                if (!x.useWorkplaceLimitation && !x.useShiftTypeLimitation) {
                    return true;
                } else if (x.useWorkplaceLimitation && x.useShiftTypeLimitation) {
                    if (
                        auditWorkplaces.some((n) => n.workplaceId == shift?.workplaceId && n.auditId == x.id) &&
                        auditShiftTypes.some((n) => n.shiftTypeId == shift?.shiftTypeId && n.auditId == x.id)
                    ) {
                        return true;
                    }
                } else if (x.useWorkplaceLimitation) {
                    if (auditWorkplaces.some((n) => n.workplaceId == shift?.workplaceId && n.auditId == x.id)) {
                        return true;
                    }
                } else if (x.useShiftTypeLimitation) {
                    if (auditShiftTypes.some((n) => n.shiftTypeId == shift?.shiftTypeId && n.auditId == x.id)) {
                        return true;
                    }
                }
                return false;
            })
            .map((x) => {
                return {
                    audit: x,
                    planItem: performedChecklists.filter((p) => p.auditId == x.id).shift() ?? null,
                };
            });
    }

    get isLoadingShiftScrapRecords(): boolean {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Pending);
    }

    get shiftScrapRecords(): ShiftScrapRecordModel[] {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Data) ?? [];
    }

    get isLoadingShiftFiles(): boolean {
        return shiftFilesStore.useGetter(CrudGetter.Pending, 'ShiftFilesForShift');
    }

    get shiftFiles(): ShiftFileModel[] {
        const items: ShiftFileModel[] = shiftFilesStore.useGetter(CrudGetter.Data, 'ShiftFilesForShift') ?? [];
        return [...items].sort((a, b) => (a.file?.fileName ?? '').localeCompare(b.file?.fileName ?? '', undefined, { sensitivity: 'base' }));
    }

    getShiftHours(): { name: string; fromDate: number; toDate: number }[] {
        const fromDate = moment(this.fromDate).startOf('hour').toDate().getTime();
        let toDate = moment(this.toDate).toDate().getTime();
        if (!moment(this.toDate).isSame(moment(this.toDate).startOf('hour'))) {
            toDate = moment(this.toDate).endOf('hour').toDate().getTime();
        }
        const items = [];
        for (let date = fromDate; date < toDate; date = date + 3600000) {
            items.push({
                name: `${moment(date).format('HH:mm')} - ${moment(date + 3600000).format('HH:mm')}`,
                fromDate: new Date(date).getTime(),
                toDate: new Date(date + 3600000).getTime(),
            });
        }
        items.push({
            name: ``,
            fromDate: new Date(toDate).getTime(),
            toDate: new Date(toDate + 3600000).getTime(),
        });
        return items;
    }

    getChartSeries(): any[] {
        const shift = shiftsStore.useGetter(CrudGetter.Detail) as ShiftModel;
        const shutdowns: PlanItemModel[] = (
            (planItemsStore.useGetter(CrudGetter.Data, 'LocationShiftDetailShutdowns') ?? []) as PlanItemModel[]
        )
            .filter((x) => x.isWorkplaceShutdown && x.shutdownFromDate)
            .map((x) => {
                if (!x.shutdownToDate) {
                    x.shutdownToDate = moment().format('YYYY-MM-DDTHH:mm:ss');
                }
                if (new Date(x.shutdownFromDate!).getTime() < new Date(this.fromDate!).getTime()) {
                    x.shutdownFromDate = moment(this.fromDate).toISOString();
                }
                if (new Date(x.shutdownToDate!).getTime() > new Date(this.toDate!).getTime()) {
                    x.shutdownToDate = moment(this.toDate).toISOString();
                }
                return x;
            })
            .filter((x: PlanItemModel) => x.shutdownFromDate && x.shutdownToDate);
        if (shutdowns.length > 0) {
            const timeline: { fromTime: Date; toTime: Date; identification: 'uptime' | 'shutdown' }[] = [];

            if (shutdowns.length > 0) {
                // Sort shutdowns by their start times
                shutdowns.sort(
                    (a: PlanItemModel, b: PlanItemModel) =>
                        new Date(a.shutdownFromDate!).getTime() - new Date(b.shutdownFromDate!).getTime()
                );

                // Merge overlapping shutdowns
                for (let i = 0; i < shutdowns.length - 1; i++) {
                    const current = shutdowns[i];
                    const next = shutdowns[i + 1];
                    const currentEnd = new Date(current.shutdownToDate!).getTime();
                    const nextStart = new Date(next.shutdownFromDate!).getTime();

                    if (currentEnd >= nextStart) {
                        current.shutdownToDate = new Date(
                            Math.max(currentEnd, new Date(next.shutdownToDate!).getTime())
                        ).toISOString();
                        shutdowns.splice(i + 1, 1);
                        i--; // Stay at the current index to check for further overlaps
                    }
                }

                let currentStart = this.fromDate!;

                for (const shutdown of shutdowns) {
                    const shutdownStart = new Date(shutdown.shutdownFromDate ?? new Date().getTime());
                    const shutdownEnd = new Date(shutdown.shutdownToDate ?? new Date().getTime());

                    // Add an uptime period before the shutdown if there's a gap
                    if (currentStart < shutdownStart) {
                        timeline.push({
                            fromTime: currentStart,
                            toTime: shutdownStart,
                            identification: 'uptime',
                        });
                    }

                    // Add the shutdown period
                    timeline.push({
                        fromTime: shutdownStart,
                        toTime: shutdownEnd,
                        identification: 'shutdown',
                    });

                    // Update the current start to be the end of the shutdown
                    currentStart = shutdownEnd > currentStart ? shutdownEnd : currentStart;
                }

                // Add an uptime period after the last shutdown if it doesn't reach the end
                if (currentStart < this.toDate!) {
                    timeline.push({
                        fromTime: currentStart,
                        toTime: this.toDate!,
                        identification: 'uptime',
                    });
                }
            } else {
                // If no shutdowns, add the entire period as uptime
                timeline.push({
                    fromTime: this.fromDate!,
                    toTime: this.toDate!,
                    identification: 'uptime',
                });
            }

            let index = 0;
            const series = timeline.map((x) => {
                const toTime = x.toTime > new Date() ? new Date() : x.toTime;
                return {
                    name: (++index).toString(),
                    identification: x.identification,
                    fromTime: x.fromTime,
                    toTime: toTime,
                    data: [toTime.getTime() - x.fromTime.getTime()],
                };
            });
            console.log(series);
            return series;
        }
        const toDate = this.toDate! > new Date() ? new Date() : this.toDate!;
        return [
            {
                name: shift.workplace!.code,
                identification: 'uptime',
                fromTime: this.fromDate!.getTime(),
                toTime: toDate!.getTime(),
                data: [toDate!.getTime() - this.fromDate!.getTime()],
            },
        ];
    }

    get isFutureShift(): boolean {
        return moment(this.shift.fromDate).isAfter(moment());
    }

    get isClosed() {
        const shift = shiftsStore.useGetter(CrudGetter.Detail) as ShiftModel;
        return shift?.shiftState == ShiftStateEnum.Closed;
    }

    get shiftProductions(): {
        item: ShiftProductionModel;
        hourlyProductions: { fromTime: string | null; toTime: string; okCount: number; nokCount: number }[];
    }[] {
        const shift = shiftsStore.useGetter(CrudGetter.Detail) as ShiftModel;
        const scrapRecords: ShiftScrapRecordModel[] = shiftScrapRecordsStore.useGetter(CrudGetter.Data) ?? [];
        const times: { fromTime: Date; toTime: Date }[] = [];
        const fromDate = new Date(shift.fromDate!);
        let toDate = new Date(shift.toDate!);
        if (toDate.getTime() > new Date().getTime()) {
            toDate = moment().add(1, 'hour').startOf('hour').toDate();
        }
        let time = fromDate;
        while (time < toDate) {
            times.push({
                fromTime: time,
                toTime: moment(time).add(60, 'minutes').isSameOrBefore(moment(toDate))
                    ? moment(time).add(60, 'minutes').toDate()
                    : moment(time).toDate(),
            });
            time = new Date(time.getTime() + 60 * 60 * 1000);
        }
        const shiftProductionGroups: {
            item: ShiftProductionModel;
            hourlyProductions: { fromTime: string | null; toTime: string; okCount: number; nokCount: number }[];
        }[] = [];
        for (const shiftProduction of shift.shiftProductions ?? []) {
            const item = { ...shiftProduction };
            item.okCount = (shiftProduction.shiftProductionRecords ?? [])
                .map((e) => e.okCount ?? 0)
                .reduce((acc, e) => acc + e, 0);
            item.nokCount = scrapRecords
                .filter((e) => e.productId == shiftProduction.productId)
                .map((e) => e.scrapCount ?? 0)
                .reduce((acc, e) => acc + e, 0);
            shiftProductionGroups.push({
                item: shiftProduction,
                hourlyProductions: times.map((x) => {
                    return {
                        fromTime: moment(x.fromTime).toISOString(),
                        toTime: moment(x.toTime).toISOString(),
                        okCount: (shiftProduction.shiftProductionRecords ?? [])
                            .filter(
                                (e) =>
                                    new Date(e.recordTime!).getTime() > x.fromTime.getTime() &&
                                    new Date(e.recordTime!).getTime() <= x.toTime.getTime()
                            )
                            .map((e) => e.okCount ?? 0)
                            .reduce((acc, e) => acc + e, 0),
                        nokCount: scrapRecords
                            .filter(
                                (e) =>
                                    e.productId == shiftProduction.productId &&
                                    new Date(e.recordTime!).getTime() > x.fromTime.getTime() &&
                                    new Date(e.recordTime!).getTime() <= x.toTime.getTime()
                            )
                            .map((e) => e.scrapCount ?? 0)
                            .reduce((acc, e) => acc + e, 0),
                    };
                }),
            });
        }
        return shiftProductionGroups;
    }

    formatFileSize(bytes?: number): string {
        if (!bytes || bytes <= 0) return '';
        if (bytes < 1024) return `${bytes} B`;
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} kB`;
        const mb = kb / 1024;
        return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
    }

    formatDateShort(iso?: string | null): string {
        if (!iso) return '';
        return moment(iso).format('DD.MM.YYYY');
    }

    loadShiftFiles() {
        shiftFilesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<ShiftFileModel>(
                { field: 'createdAt', index: 1, order: 'desc' },
                [{ field: 'shiftId', op: 'eq', comparand: this.shiftId }],
                undefined,
                false,
                'ShiftFilesForShift'
            )
        );
    }

    onUploadShiftFilesClick() {
        this.inputShiftFiles.click();
    }

    async onShiftFilesInputChanged() {
        let canContinue = true;
        for (const file of this.inputShiftFiles.files) {
            const fileSize = file.size as number;
            if (fileSize > this.maxUploadFileSize * 1000000) {
                canContinue = false;
                this.snackbarFileTooBig = true;
                break;
            }
        }
        if (!canContinue) {
            return;
        }
        this.isUploadingShiftFile = true;
        try {
            for (let inputFile of this.inputShiftFiles.files) {
                const originalName = inputFile.name;
                if (
                    inputFile.type == 'image/jpeg' ||
                    inputFile.type == 'image/jpg' ||
                    inputFile.type == 'image/png' ||
                    inputFile.type == 'image/webp'
                ) {
                    const options: any = {
                        useWebWorker: false,
                        preserveExif: true,
                        maxIteration: 5,
                    };
                    const tenantId = authStore.useGetter(AuthGetter.GetTenant);
                    const tenant = (tenantsStore.useGetter(CrudGetter.Data) as TenantModel[]).first((x) => x.id == tenantId);
                    if (tenant?.imageUploadUseCompression == true) {
                        if (
                            tenant.imageUploadMaxFileSize &&
                            !isNaN(tenant.imageUploadMaxFileSize) &&
                            Number(tenant.imageUploadMaxFileSize) > 0
                        ) {
                            options.maxSizeMB = Number(tenant.imageUploadMaxFileSize);
                        }
                        if (
                            tenant.imageUploadMaxResolution &&
                            !isNaN(tenant.imageUploadMaxResolution) &&
                            Number(tenant.imageUploadMaxResolution) > 0
                        ) {
                            options.maxWidthOrHeight = Number(tenant.imageUploadMaxResolution);
                        }
                        if (
                            tenant.imageUploadQuality &&
                            !isNaN(tenant.imageUploadQuality) &&
                            Number(tenant.imageUploadQuality) >= 1 &&
                            Number(tenant.imageUploadQuality) < 100
                        ) {
                            options.initialQuality = Number(tenant.imageUploadQuality) / 100;
                        }
                    }
                    inputFile = await imageCompression(inputFile, options);
                    Object.defineProperty(inputFile, 'name', { writable: true, value: originalName });
                }
                const formData = new FormData();
                formData.append('file', inputFile, originalName);
                const tenantId = authStore.useGetter(AuthGetter.GetTenant);
                const token = getAccessToken();
                const r = await axios.post((window as any).ApiService.baseUrl + '/api/files/upload', formData, {
                    headers: {
                        TenantId: tenantId,
                        Authorization: `Bearer ${token}`,
                    },
                });
                const fileModel = r.data.returnValue as FileModel;
                await this.onShiftFileUploaded(fileModel);
            }
        } finally {
            this.inputShiftFiles.value = '';
            this.isUploadingShiftFile = false;
        }
    }

    async onShiftFileUploaded(fileModel: FileModel) {
        const item: ShiftFileModel = {
            shiftId: this.shiftId!,
            fileId: fileModel.id,
            file: fileModel,
        } as ShiftFileModel;
        // Persist přes store na jiný klíč, aby se nespustil re-fetch aktivního listu
        shiftFilesStore.dispatch(CrudAction.Create, { item, key: 'ShiftFilesForShiftPersist' });
        const createdItem = (await this.subscribe(
            shiftFilesStore,
            CrudReponse.Create,
            'ShiftFilesForShiftPersist'
        )) as ShiftFileModel;
        // Lokální doplnění do zobrazeného seznamu bez re-fetch
        shiftFilesStore.dispatch(CrudAction.Create, {
            item: createdItem,
            key: 'ShiftFilesForShift',
            extra: { local: true },
        });
    }

    downloadFile(file?: FileModel): void {
        if (!file) return;
        const tenantId = authStore.useGetter(AuthGetter.GetTenant);
        const token = getAccessToken();
        axios({
            url: (window as any).ApiService.baseUrl + '/api/files/' + file.id + '/download',
            method: 'GET',
            responseType: 'blob',
            headers: {
                TenantId: tenantId,
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.fileName || 'file');
            document.body.appendChild(link);
            link.click();
        });
    }

    async removeShiftFile(sf: ShiftFileModel) {
        if (!sf.id) return;
        this.$set(this.deletingShiftFile, sf.id, true);
        try {
            shiftFilesStore.dispatch(CrudAction.Delete, { id: sf.id, key: 'ShiftFilesForShift' });
            await this.subscribe(shiftFilesStore, CrudReponse.Delete, 'ShiftFilesForShift');
        } finally {
            this.$set(this.deletingShiftFile, sf.id, false);
        }
    }

    @Watch('shiftId')
    shiftIdChanged(v: string | null, p: string | null) {
        if (v != p && v != null) {
            this.loadShift();
        }
    }

    mounted() {
        if (this.shiftId) {
            this.loadShift();
        }
        const tenantId = authStore.useGetter(AuthGetter.GetTenant);
        const tenant = (tenantsStore.useGetter(CrudGetter.Data) as TenantModel[]).first((x) => x.id == tenantId);
        this.maxUploadFileSize = tenant?.maxUploadFileSize ? tenant.maxUploadFileSize : 50;
        this.$store.subscribe((mutation) => {
            if (mutation.type === shiftsStore.getActionName(CrudResponseAction.UpdateSuccess)) {
                if (mutation.payload.item.id == this.shiftId) {
                    this.fromDate = new Date(mutation.payload.item.fromDate!);
                    this.toDate = new Date(mutation.payload.item.toDate!);
                    if (!this.commentIsChanged) {
                        this.comment = mutation.payload.item.comment;
                    }
                    setTimeout(() => {
                        try {
                            this.chart?.refresh();
                        } catch (e) {
                            console.log(e);
                        }
                        try {
                            this.chart?.updateOptions();
                        } catch (e) {
                            console.log(e);
                        }
                        try {
                            this.chart?.updateSeries();
                        } catch (e) {
                            console.log(e);
                        }
                    }, 100);
                }
            } else if (mutation.type === shiftsStore.getActionName(CrudResponseAction.PartialUpdateSuccess)) {
                if (mutation.payload.item.id == this.shiftId) {
                    this.fromDate = new Date(mutation.payload.item.fromDate!);
                    this.toDate = new Date(mutation.payload.item.toDate!);
                    if (!this.commentIsChanged) {
                        this.comment = mutation.payload.item.comment;
                    }
                    setTimeout(() => {
                        try {
                            this.chart?.refresh();
                        } catch (e) {
                            console.log(e);
                        }
                        try {
                            this.chart?.updateOptions();
                        } catch (e) {
                            console.log(e);
                        }
                        try {
                            this.chart?.updateSeries();
                        } catch (e) {
                            console.log(e);
                        }
                    }, 100);
                }
            } else if (mutation.type === shiftsStore.getActionName(CrudResponseAction.DeleteSuccess)) {
                if (mutation.payload.data.shift()?.id == this.shiftId) {
                    this.onCloseClick();
                }
            }
        });
    }

    loadShift() {
        this.fromDate = null;
        this.toDate = null;
        this.commentIsChanged = false;
        shiftsStore.dispatch(CrudAction.Get, {
            id: this.shiftId,
        });
        this.subscribe(shiftsStore, CrudReponse.Get).then((e: ShiftModel) => {
            this.fromDate = new Date(e.fromDate!);
            this.toDate = new Date(e.toDate!);
            this.comment = e.comment ?? '';
            this.loadIssueSolving(e.fromDate!, e.toDate!, e.workplaceId!);
            this.loadPerformedMaintenance(e.fromDate!, e.toDate!, e.workplaceId!);
            this.loadShutdowns(e.fromDate!, e.toDate!, e.workplaceId!);
            this.loadAvailableChecklists();
            this.loadPerformedChecklists();
            this.loadShiftStations();
            this.loadShiftScrapRecords();
            this.loadShiftFiles();
        });
    }

    loadIssueSolving(fromDate: string, toDate: string, workplaceId: string) {
        planItemsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PlanItemModel>(
                { field: 'time', index: 1, order: 'asc' },
                [
                    {
                        field: 'processType',
                        op: 'eq',
                        comparand: ProcessTypeEnum.IssueSolving,
                    },
                    {
                        field: 'time',
                        op: 'gte',
                        comparand: fromDate,
                    },
                    {
                        field: 'time',
                        op: 'lte',
                        comparand: toDate,
                    },
                    {
                        field: 'workplaceId',
                        op: 'eq',
                        comparand: workplaceId,
                    },
                ],
                undefined,
                false,
                'LocationShiftDetail'
            )
        );
    }

    loadPerformedMaintenance(fromDate: string, toDate: string, workplaceId: string) {
        planItemsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PlanItemModel>(
                { field: 'time', index: 1, order: 'asc' },
                [
                    {
                        field: 'processType',
                        op: 'eq',
                        comparand: ProcessTypeEnum.Maintenance,
                    },
                    {
                        field: 'time',
                        op: 'gte',
                        comparand: fromDate,
                    },
                    {
                        field: 'time',
                        op: 'lte',
                        comparand: toDate,
                    },
                    {
                        field: 'workplaceId',
                        op: 'eq',
                        comparand: workplaceId,
                    },
                ],
                undefined,
                false,
                'LocationShiftDetailMaintenance'
            )
        );
    }

    loadShutdowns(fromDate: string, toDate: string, workplaceId: string) {
        planItemsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<PlanItemModel>(
                { field: 'time', index: 1, order: 'asc' },
                [
                    {
                        field: 'IsWorkplaceShutdown',
                        op: 'eq',
                        comparand: true,
                    },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'shutdownToDate',
                                        op: 'gt',
                                        comparand: fromDate,
                                    },
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lt',
                                        comparand: toDate,
                                    },
                                ],
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'shutdownFromDate',
                                        op: 'lt',
                                        comparand: toDate,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                    {
                        field: 'workplaceId',
                        op: 'eq',
                        comparand: workplaceId,
                    },
                ],
                undefined,
                false,
                'LocationShiftDetailShutdowns'
            )
        );
    }

    loadAvailableChecklists() {
        auditsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'name', index: 1, order: 'asc' },
                [
                    {
                        field: 'processType',
                        op: 'eq',
                        comparand: ProcessTypeEnum.Checklist,
                    },
                ],
                undefined,
                false,
                'AvailableChecklists'
            )
        );
        auditWorkplacesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: 'asc' },
                [{ field: 'audit.processType', op: 'eq', comparand: ProcessTypeEnum.Checklist }],
                undefined,
                false,
                'AvailableChecklists'
            )
        );
        auditShiftTypesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                { field: 'id', index: 1, order: 'asc' },
                [{ field: 'audit.processType', op: 'eq', comparand: ProcessTypeEnum.Checklist }],
                undefined,
                false,
                'AvailableChecklists'
            )
        );
    }

    loadPerformedChecklists() {
        planItemsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'createdAt', order: 'asc', index: 1 },
                [
                    { field: 'processType', op: 'eq', comparand: ProcessTypeEnum.Checklist },
                    { field: 'shiftId', op: 'eq', comparand: this.shiftId },
                ],
                undefined,
                false,
                'PerformedChecklists'
            )
        );
    }

    loadShiftStations() {
        shiftStationsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<ShiftStationModel>(
                undefined,
                [{ field: 'shiftId', op: 'eq', comparand: this.shiftId }],
                undefined,
                false,
                'LocationShiftDetail'
            )
        );
    }

    protected loadShiftScrapRecords() {
        shiftScrapRecordsStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload(
                {
                    field: 'recordTime',
                    index: 1,
                    order: 'asc',
                },
                [{ field: 'shiftId', op: 'eq', comparand: this.shiftId }],
                undefined,
                false
            )
        );
    }

    async onShiftLockClick() {
        this.isSaving = true;
        shiftsStore.dispatch(CrudAction.Get, {
            id: this.shiftId,
        });
        await this.subscribe(shiftsStore, CrudReponse.Get);
        const tenantUser = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
        const item = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
        item.shiftState = ShiftStateEnum.Closed;
        item.closedById = tenantUser.id;
        item.closedBy = tenantUser;
        item.closedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
        shiftsStore.dispatch(CrudAction.Update, { item });
        await this.subscribe(shiftsStore, CrudReponse.Update);
        this.isSaving = false;
    }

    async onShiftUnlockClick() {
        this.isSaving = true;
        shiftsStore.dispatch(CrudAction.Get, {
            id: this.shiftId,
        });
        await this.subscribe(shiftsStore, CrudReponse.Get);
        const item = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
        item.shiftState = ShiftStateEnum.Opened;
        item.closedById = undefined;
        item.closedBy = undefined;
        item.closedAt = undefined;
        shiftsStore.dispatch(CrudAction.Update, { item });
        await this.subscribe(shiftsStore, CrudReponse.Update);
        this.isSaving = false;
    }

    async onSaveComment() {
        const comment = this.comment;
        this.isSavingComment = true;
        shiftsStore.dispatch(CrudAction.Get, {
            id: this.shiftId,
        });
        await this.subscribe(shiftsStore, CrudReponse.Get);
        const item = { ...shiftsStore.useGetter(CrudGetter.Detail) } as ShiftModel;
        item.comment = comment;
        shiftsStore.dispatch(CrudAction.Update, { item });
        await this.subscribe(shiftsStore, CrudReponse.Update);
        this.isSavingComment = false;
        this.commentIsChanged = false;
    }

    onCloseDialog() {
        this.editDialog = false;
    }

    onCloseClick() {
        this.$emit('close');
        this.editDialog = false;
    }

    onEditRecordsClick(tab: number) {
        this.editDialogTab = tab;
        this.$nextTick(() => {
            this.editDialog = true;
        });
    }

    async onPrintShift() {
        this.isPrinting = true;
        try {
            const e = await ApiService.api.printShiftById(this.shiftId!);
            const res = e.data.returnValue as FileDownloadModel;
            const blob = b64toBlob(res.base64!, res.contentType!);
            const tab = window.open();
            tab!.location.href = window.URL.createObjectURL(blob);
        } catch (ex) {
            console.error(ex);
        } finally {
            this.isPrinting = false;
        }
    }

    onCommentChanged(e: string) {
        const shift = shiftsStore.useGetter(CrudGetter.Detail) as ShiftModel;
        this.commentIsChanged = (e ?? '') !== (shift?.comment ?? '');
    }

    onShiftScrapRecordClick() {
        this.activeScrapRecordId = null;
        this.shiftScrapRecordDialog = true;
    }

    onEditScrapRecordClick(scrapRecord: ShiftScrapRecordModel) {
        this.activeScrapRecordId = scrapRecord!.id!;
        this.shiftScrapRecordDialog = true;
    }

    onDeleteScrapRecordClick(scrapRecord: ShiftScrapRecordModel) {
        this.activeScrapRecordToDelete = scrapRecord;
        this.shiftScrapRecordDeleteDialog = true;
    }

    async deleteScrapRecord() {
        shiftScrapRecordsStore.dispatch(CrudAction.Delete, {
            id: this.activeScrapRecordToDelete!.id,
        });
        await this.subscribe(shiftScrapRecordsStore, CrudReponse.Delete);
        this.shiftScrapRecordDeleteDialog = false;
        this.activeScrapRecordToDelete = null;
    }

    hasSomeNonNullValues(hourlyProductions: { okCount: number; nokCount: number }[]) {
        return hourlyProductions.some((e) => e.okCount > 0 || e.nokCount > 0);
    }
}
